/**
 * CLE-2811 — what a wizard step shows while the first pass is still running: a one-line explanation of
 * where the worker is, then either what it has read so far (plans on Lines of Business, each plan's
 * terms on Contract Terms — read-only previews, from the progress's `partial`) or skeleton placeholders.
 * The real, editable recommendations take their place when the pass finishes.
 */

import { Checkbox } from "primereact/checkbox";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import React from "react";
import styled from "styled-components";

import {
  STEP_FILLS_IN,
  LOB_CHOOSE_AFTER,
  PREVIEW_CONTRACT_FACTS,
  PREVIEW_FAILED_DETAIL,
  PREVIEW_NOT_SAVED,
  PREVIEW_OPEN_QUESTIONS,
  PREVIEW_STATUS_LABELS,
  previewContractType,
} from "../copy";
import {
  IPartialPreview,
  IPreviewField,
  PreviewStatus,
  plansLine,
  termsLine,
} from "./partialPreview";
import { StepId } from "./steps";

/** The one line under the step's heading: where the worker is, in words. */
export function PreviewLine({ text }: { text: string }) {
  return (
    <p className="mt-0 mb-3 text-sm text-color-secondary flex align-items-center gap-2">
      <i
        className="pi pi-spin pi-spinner"
        style={{ fontSize: "0.8rem", color: "var(--primary-color)" }}
        aria-hidden
      />
      <span>{text}</span>
    </p>
  );
}

/** Placeholder cards in the shape of what will arrive. */
export function StepSkeleton({ cards = 2 }: { cards?: number }) {
  return (
    <div
      className="flex flex-column gap-3"
      aria-hidden
      data-testid="step-skeleton"
    >
      {Array.from({ length: cards }, (_, i) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          className="surface-card border-1 surface-border border-round p-3"
        >
          <Skeleton width="40%" height="1.1rem" className="mb-3" />
          <Skeleton width="90%" height="0.8rem" className="mb-2" />
          <Skeleton width="75%" height="0.8rem" className="mb-2" />
          <Skeleton width="60%" height="0.8rem" />
        </div>
      ))}
    </div>
  );
}

const STATUS_SEVERITY: Record<
  PreviewStatus,
  "info" | "success" | "danger" | undefined
> = {
  pending: undefined,
  extracting: "info",
  done: "success",
  failed: "danger",
};

/** A plan's status as the worker reports it. CLE-2811 — also chips a term's still-extracting schedule. */
export function PreviewStatusTag({ status }: { status: PreviewStatus }) {
  return (
    <Tag
      value={PREVIEW_STATUS_LABELS[status]}
      severity={STATUS_SEVERITY[status]}
      icon={status === "extracting" ? "pi pi-spin pi-spinner" : undefined}
      rounded
      className={status === "pending" ? "surface-200 text-700" : undefined}
      style={{ fontSize: "0.72rem" }}
    />
  );
}

/** A preview card: dashed, so it never reads as a saved, editable recommendation. */
const PreviewCard = styled.div<{ $muted?: boolean }>`
  padding: 0.9rem 1rem;
  border: 1px dashed var(--surface-400, #bdbdbd);
  border-radius: 8px;
  background: var(--surface-card, #ffffff);
  opacity: ${({ $muted }) => ($muted ? 0.75 : 1)};
`;

const Fields = styled.dl`
  display: grid;
  grid-template-columns: minmax(9rem, max-content) minmax(0, 1fr);
  gap: 0.35rem 1rem;
  margin: 0.75rem 0 0;
  font-size: 0.85rem;

  dt {
    color: var(--text-color-secondary);
  }

  dd {
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;

const NotSaved = styled.span`
  margin-left: auto;
  font-size: 0.72rem;
  color: var(--text-color-secondary);
  white-space: nowrap;
`;

function FieldList({ fields }: { fields: IPreviewField[] }) {
  return (
    <Fields>
      {fields.map((f) => (
        <React.Fragment key={f.label}>
          <dt>{f.label}</dt>
          <dd>{f.value}</dd>
        </React.Fragment>
      ))}
    </Fields>
  );
}

// ---------------------------------------------------------------------------------------------
// Lines of Business
// ---------------------------------------------------------------------------------------------

interface IPlanCardsProps {
  plans: { key: string; name: string; status: PreviewStatus }[];
}

/** The plans found so far, as the picker's cards — ticked, and not changeable until the pass is done. */
export function PlanPreviewCards({ plans }: IPlanCardsProps) {
  return (
    <div>
      <div className="grid">
        {plans.map((plan) => (
          <div className="col-12 md:col-6" key={plan.key}>
            <div className="surface-card border-round p-3 border-1 surface-border h-full">
              <div className="flex align-items-center gap-2 flex-wrap">
                <Checkbox checked readOnly disabled />
                <span className="font-medium">{plan.name}</span>
                <span className="ml-auto">
                  <PreviewStatusTag status={plan.status} />
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <small className="block mt-2 text-color-secondary">
        {LOB_CHOOSE_AFTER}
      </small>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Contract Terms
// ---------------------------------------------------------------------------------------------

interface ITermPreviewCardsProps {
  contractType: string | null;
  facts: IPreviewField[];
  terms: {
    key: string;
    name: string;
    status: PreviewStatus;
    fields: IPreviewField[];
    openQuestions: string[];
  }[];
}

/** The contract facts, then one card per plan, filled as each plan's terms are read. */
export function TermPreviewCards({
  contractType,
  facts,
  terms,
}: ITermPreviewCardsProps) {
  return (
    <div className="flex flex-column gap-3">
      {contractType ? (
        <div>
          <Tag
            value={previewContractType(contractType)}
            severity="info"
            rounded
          />
        </div>
      ) : null}

      {facts.length ? (
        <PreviewCard data-testid="preview-facts">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-file text-color-secondary" />
            <span className="font-semibold">{PREVIEW_CONTRACT_FACTS}</span>
            <NotSaved>{PREVIEW_NOT_SAVED}</NotSaved>
          </div>
          <FieldList fields={facts} />
        </PreviewCard>
      ) : null}

      {terms.map((term) => (
        <PreviewCard
          key={term.key}
          $muted={term.status === "pending"}
          data-testid="preview-term"
        >
          <div className="flex align-items-center gap-2 flex-wrap">
            <i className="pi pi-building text-color-secondary" />
            <span className="font-semibold">{term.name}</span>
            <PreviewStatusTag status={term.status} />
            <NotSaved>{PREVIEW_NOT_SAVED}</NotSaved>
          </div>
          {term.status === "done" && term.fields.length ? (
            <FieldList fields={term.fields} />
          ) : null}
          {term.status === "extracting" ? (
            <div className="mt-3" aria-hidden>
              <Skeleton width="80%" height="0.8rem" className="mb-2" />
              <Skeleton width="65%" height="0.8rem" />
            </div>
          ) : null}
          {term.status === "failed" ? (
            <p className="mt-2 mb-0 text-sm text-color-secondary">
              {PREVIEW_FAILED_DETAIL}
            </p>
          ) : null}
          {term.openQuestions.length ? (
            <div className="mt-3 text-sm">
              <div className="font-medium mb-1">{PREVIEW_OPEN_QUESTIONS}</div>
              <ul className="m-0 pl-3">
                {term.openQuestions.map((q) => (
                  <li key={q}>{q}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </PreviewCard>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// The step body during a first pass
// ---------------------------------------------------------------------------------------------

interface IGenerationStepPreviewProps {
  step: StepId;
  preview: IPartialPreview | null;
}

/**
 * What a step shows while the first pass runs: Lines of Business the plans found so far, Contract Terms
 * the facts and each plan's terms, any other step a skeleton — each under a line saying where the worker
 * is. With no `partial` at all, every step keeps its skeleton.
 */
export function GenerationStepPreview({
  step,
  preview,
}: IGenerationStepPreviewProps) {
  if (step === "segments") {
    return (
      <div data-testid="generation-preview">
        <PreviewLine text={plansLine(preview)} />
        {preview?.plans.length ? (
          <PlanPreviewCards plans={preview.plans} />
        ) : (
          <StepSkeleton />
        )}
      </div>
    );
  }
  if (step === "terms") {
    const hasContent =
      !!preview && (preview.terms.length > 0 || preview.facts.length > 0);
    return (
      <div data-testid="generation-preview">
        <PreviewLine text={termsLine(preview)} />
        {preview ? (
          <TermPreviewCards
            contractType={preview.contractType}
            facts={preview.facts}
            terms={preview.terms}
          />
        ) : null}
        {!hasContent ? (
          <div className={preview ? "mt-3" : undefined}>
            <StepSkeleton />
          </div>
        ) : null}
      </div>
    );
  }
  return (
    <div data-testid="generation-preview">
      <PreviewLine text={STEP_FILLS_IN} />
      <StepSkeleton />
    </div>
  );
}
